import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { createSale } from "../../api/salesApi";
import ItemSelector from "./ItemSelector";
import PaymentSplit from "./PaymentSplit";
import RetailerAutocomplete from "../../components/ui/RetailerAutocomplete";
import { ProductsAPI } from "../../api/productApi";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import Subnav from "./SubNav";
import { useAlert } from "../../context/AlertContext";
import DateInput from "../../components/ui/DateInput";

const schema = Yup.object({
  items: Yup.array().min(1, "At least one item required"),
});

export default function SaleForm() {
  const [retailer, setRetailer] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState({});

  const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const calcTotals = (items = [], payments = {}) => {
    const itemsTotal = items.reduce(
      (sum, it) => sum + toNumber(it.price_per_unit) * toNumber(it.quantity),
      0
    );
    const paymentsTotal = Object.values(payments).reduce(
      (sum, v) => sum + toNumber(v),
      0
    );
    const due = Math.max(0, itemsTotal - paymentsTotal);
    const overpay = Math.max(0, paymentsTotal - itemsTotal);
    return { itemsTotal, paymentsTotal, due, overpay };
  };
  const { itemsTotal, paymentsTotal, due, overpay } = calcTotals(
    items,
    payments
  );

  const today = new Date();
  const thirtyDaysAgo = new Date();
  const thirtyDaysAfter = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  thirtyDaysAfter.setDate(today.getDate() + 30);

  const formatDate = (date) => date.toISOString().split("T")[0];


  const { showAlert } = useAlert();
  const formik = useFormik({
    initialValues: { notes: "", saleDate: formatDate(today) },
    validationSchema: schema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const selectedDate = new Date(values.saleDate);

        if (selectedDate < thirtyDaysAgo || selectedDate > today) {
          showAlert({ message: "Date must be within the last 30 days" + res.error, type: "error" });
          return;
        }

        const payload = {
          retailerId: retailer?.id,
          items,
          paymentDetails: payments,
          offers: [], // you can fetch and pass selected offers
          notes: values.notes,
          saleDate: selectedDate,
        };
        const { ok, message } = validateSale({ retailer, items, payments });
        if (!ok) {
          alert(message);
          return;
        }

        const res = await createSale(payload);
        if (res?.error) {
          // API rejected (ex: insufficient stock, credit limit exceeded, etc.)
          showAlert({ message: "Error: " + res.error, type: "error" });
          return;
        }
        showAlert({ message: "Sale created successfully", type: "success" });
        resetForm();
        setRetailer(null);
        setItems([]);
        setPayments({});
      } catch (err) {
        showAlert({ message: "Error: " + err.message, type: "error" });
      }
    },
  });
  const [products, setProducts] = useState([]);
  const getProducts = async () => {
    const [list] = await Promise.all([ProductsAPI.list()]);
    setProducts(list || []);
  };
  useEffect(() => {
    getProducts();
  }, []);

  // ---- reusable validation ----
  const validateSale = ({ retailer, items, payments }) => {
    if (!retailer?.id)
      return { ok: false, message: "Please select a retailer." };
    if (!Array.isArray(items) || items.length === 0)
      return { ok: false, message: "Please add at least one item." };

    for (let i = 0; i < items.length; i++) {
      const it = items[i] || {};
      const row = i + 1;
      if (!it.product_id)
        return { ok: false, message: `Item #${row}: missing product.` };

      // quantity: integer >= 1
      const qty = toNumber(it.quantity);
      if (!(qty > 0) || !Number.isFinite(qty))
        return { ok: false, message: `Item #${row}: quantity must be ≥ 1.` };
      if (!Number.isInteger(qty))
        return {
          ok: false,
          message: `Item #${row}: quantity must be a whole number.`,
        };

      // price: number >= 0
      const price = toNumber(it.price_per_unit);
      if (!(price >= 0) || !Number.isFinite(price))
        return { ok: false, message: `Item #${row}: price must be ≥ 0.` };

      // unit: if present, enforce allowed values
      if (it.unit && !["bottle", "case"].includes(it.unit))
        return {
          ok: false,
          message: `Item #${row}: unit must be "bottle" or "case".`,
        };
    }

    // payments: non-negative, numeric
    for (const [k, v] of Object.entries(payments || {})) {
      const amt = toNumber(v);
      if (amt < 0)
        return { ok: false, message: `${k} amount cannot be negative.` };
      if (!Number.isFinite(amt))
        return { ok: false, message: `${k} amount must be a valid number.` };
    }

    const { itemsTotal, overpay } = calcTotals(items, payments);
    if (!(itemsTotal > 0))
      return { ok: false, message: "Items total must be greater than ₹0.00." };
    if (overpay > 0)
      return {
        ok: false,
        message: `Overpayment of ₹${overpay.toFixed(
          2
        )}. Please adjust the split.`,
      };

    // Looks good
    return { ok: true };
  };

  return (
    <div>
      <Navbar />
      <Subnav />
      <form
        onSubmit={formik.handleSubmit}
        className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-xl space-y-6 mb-10"
      >
        <h2 className="text-2xl font-semibold">Create Sale</h2>
        <div className="inline-flex items-center w-full gap-2 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-2 text-sm text-amber-700 font-medium shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
            />
          </svg>
          <span>Once a sale is created cannot be edited.</span>
        </div>

        <div>
          <label className="block font-medium mb-1">Retailer</label>
          <RetailerAutocomplete value={retailer} onChange={setRetailer} />
        </div>

        <DateInput
          label="Sale Date"
          min={formatDate(thirtyDaysAgo)}
          max={formatDate(thirtyDaysAfter)}
          className="border rounded px-3 py-2"
          value={formik.values.saleDate}
          onChange={(e) => formik.setFieldValue("saleDate", formatDate(new Date(e.target.value)))}
        />
        <div className="z-[10] relative">
          <ItemSelector items={items} setItems={setItems} products={products} />
          {formik.errors.items && (
            <p className="text-red-600">{formik.errors.items}</p>
          )}
        </div>

        <div className="z-[1] relative">
          <label className="block font-medium mb-1">Payments</label>
          <PaymentSplit payments={payments} setPayments={setPayments} />
        </div>

        <div className="mb-20">
          <label className="block font-medium mb-1">Notes</label>
          <textarea
            name="notes"
            className="w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
            value={formik.values.notes}
            onChange={formik.handleChange}
          />
        </div>
        <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-base font-semibold text-gray-900">Totals</h3>
          </div>

          <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-600">Items Total</div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums">
                ₹{itemsTotal.toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-600">Payments Total</div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums">
                ₹{paymentsTotal.toFixed(2)}
              </div>
            </div>
            <div
              className={`rounded-xl border px-4 py-3 ${overpay
                ? "border-amber-200 bg-amber-50"
                : "border-emerald-200 bg-emerald-50"
                }`}
            >
              <div className="text-xs text-gray-600">
                {overpay ? "Overpayment" : "Due"}
              </div>
              <div
                className={`mt-0.5 text-lg font-semibold tabular-nums ${overpay ? "text-amber-700" : "text-emerald-700"
                  }`}
              >
                ₹{(overpay || due).toFixed(2)}
              </div>
            </div>
          </div>

          {overpay > 0 && (
            <p className="px-4 pb-4 text-xs text-amber-700">
              Payments exceed the total by ₹{overpay.toFixed(2)}. Adjust the
              split or allow change/credit.
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting}
          className="w-full mt-4 items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 active:scale-95"
        >
          Confirm Sale
        </button>
      </form>
      <Footer />
    </div>
  );
}
